import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Container, Paper, Button, Collapse } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HeroContainer from './HeroContainer';
import { Box } from '@mui/system';
import FlightSearch from './FlightSearch';
import TripComWidget from './TripComWidget';
import TripDotComSearch from './TripDotComSearch';
import TripPayoutsWidget from './TripPayoutsWidget';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { FlightSearchProvider } from '~/types/flights';
import HeroMenuButtons, { MenuItemKey } from './HeroMenuButtons';

type Props = {
  showTripCom?: boolean
  tripComProps?: React.ComponentProps<typeof TripComWidget>
  flightSearchProvider?: FlightSearchProvider
  menuItems?: MenuItemKey[]
  onPayoutsSearchClick?: () => void
  widgetOnly?: boolean
  forceShowPayoutsSearch?: boolean
  hideMobilePayoutsChangeButton?: boolean
}

const FlightSearchHero: React.FC<Props> = ({ showTripCom = false, tripComProps, flightSearchProvider, menuItems = [], onPayoutsSearchClick, widgetOnly = false, forceShowPayoutsSearch = false, hideMobilePayoutsChangeButton = false }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactHero = useMediaQuery('(max-width:1111px)');
  const isMobileSearchToggleEnabled = useMediaQuery('(max-width:899px)');
  const [showCompactSearch, setShowCompactSearch] = useState(true);

  const isTravelPayoutsSearch = flightSearchProvider === FlightSearchProvider.TRAVELPAYOUTS;

  useEffect(() => {
    if (!isMobileSearchToggleEnabled) {
      setShowCompactSearch(true);
    }
  }, [isMobileSearchToggleEnabled]);

  useEffect(() => {
    if (forceShowPayoutsSearch) {
      setShowCompactSearch(true);
    }
  }, [forceShowPayoutsSearch]);

  const handleTravelPayoutsSearchClick = useCallback(() => {
    if (isMobileSearchToggleEnabled) {
      setShowCompactSearch(false);
    }
    onPayoutsSearchClick?.();
  }, [isMobileSearchToggleEnabled, onPayoutsSearchClick]);

  const shouldShowSearchPanel = forceShowPayoutsSearch || !isTravelPayoutsSearch || !isMobileSearchToggleEnabled || showCompactSearch;

  // Determine which search form to display
  const getSearchComponent = () => {
    // Handle backward compatibility with showTripCom prop
    if (showTripCom && !flightSearchProvider) {
      return <TripComWidget width={1000} height={222} {...(tripComProps || {})} />;
    }

    // Travelpayouts white-label widget is functional, not GA/ads tracking.
    // Render it immediately like the other search providers; GA/event tracking
    // remains separately gated behind optional-cookie consent.
    if (flightSearchProvider === FlightSearchProvider.TRAVELPAYOUTS) {
      return <TripPayoutsWidget wlId={"17957"} width="100%" onSearchClick={handleTravelPayoutsSearchClick} />;
    }

    // Use flightSearchProvider enum if specified
    if (flightSearchProvider === FlightSearchProvider.TRIP_DOT_COM) {
      return <TripDotComSearch />;
    }

    // Default to Tripenger search form
    return <FlightSearch />;
  };

  return (
    <HeroContainer
      backgroundImage="/media/content/home-banner.jpg"
      height={'auto'}
    >
      <Box bgcolor={'rgba(5, 29, 64, .9)'} width={'100%'} height={'100%'}>
        <Container>
          {!widgetOnly && (
            <Box pt={isCompactHero ? 4 : 16} pb={isCompactHero ? 4 : 10}>
              {menuItems.length > 0 && <HeroMenuButtons menuItems={menuItems} />}
              <Typography
                color={'#fff'}
                variant={isSmallScreen ? 'h4' : 'h3'}
                pb={isSmallScreen ? 1.5 : 3} // Adjust padding-bottom based on screen size
                mt={3}
              >
                {isSmallScreen
                  ? 'Your flight begins here!' // Show short text on small screens
                  : 'Your flight begins here!\nCompare flights and discover your next trip.'}
              </Typography>
            </Box>
          )}
          <Box position={'relative'} pt={widgetOnly ? 2 : (isCompactHero ? 0 : 10)} pb={isCompactHero ? 4 : 0}>
            <Box
              position={isCompactHero ? 'static' : 'absolute'}
              left={0}
              bottom={isCompactHero ? 'auto' : -60}
              width="100%"
            >
              {isTravelPayoutsSearch && isMobileSearchToggleEnabled && !showCompactSearch && !hideMobilePayoutsChangeButton && (
                <Box display="flex" justifyContent="center" mb={2.5} px={1}>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<TuneIcon />}
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={() => setShowCompactSearch(true)}
                    sx={{
                      color: '#061b33',
                      minHeight: 52,
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: 420,
                      borderRadius: 999,
                      px: 3,
                      py: 1.45,
                      fontSize: 15,
                      fontWeight: 900,
                      letterSpacing: '-0.01em',
                      textTransform: 'none',
                      justifyContent: 'center',
                      boxShadow: '0 18px 45px rgba(0, 98, 227, 0.32), 0 4px 12px rgba(2, 18, 38, 0.18)',
                      border: '1px solid rgba(255, 255, 255, 0.85)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f4f9ff 52%, #eaf4ff 100%)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ffffff 0%, #edf6ff 48%, #dfefff 100%)',
                        boxShadow: '0 22px 52px rgba(0, 98, 227, 0.38), 0 6px 16px rgba(2, 18, 38, 0.22)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&:focus-visible': {
                        outline: '3px solid rgba(0, 98, 227, 0.35)',
                        outlineOffset: 3,
                      },
                      '& .MuiButton-startIcon': {
                        color: '#0062e3',
                        mr: 1,
                      },
                      '& .MuiButton-endIcon': {
                        color: '#0062e3',
                        ml: 0.25,
                      },
                    }}
                    aria-expanded={showCompactSearch}
                    aria-controls="flight-search-details-panel"
                  >
                    Change search details
                  </Button>
                </Box>
              )}
              <Collapse in={shouldShowSearchPanel} timeout={260} unmountOnExit={false}>
                <Paper
                  id="flight-search-details-panel"
                  sx={{
                    bgcolor: '#fff',
                    padding: '15px 20px',
                    borderRadius: 5,
                  }}
                  elevation={4}
                >
                  {getSearchComponent()}
                </Paper>
              </Collapse>
            </Box>
          </Box>
        </Container>
      </Box>
    </HeroContainer>
  );
};

export default FlightSearchHero;
